import React from "react";
import { useNavigate } from "react-router-dom";
import { FiShoppingCart } from "react-icons/fi";
import { BsCartCheck } from "react-icons/bs";
import Swal from "sweetalert2";

import { formatCurrency, getImage, parseLocaleNumber, truncateCaractere } from "../../utils/Utils";
import {
  getCatalogPriceInfo,
  getCatalogSelectionLabel,
  hasProductStock,
  isVariationInStock,
  needsVariationSelection,
} from "../../utils/catalogProductPrice";
import { useShopTvaSettings } from "../../catalog/useShopDevise";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import {
  addCartProduct,
  getCartCount,
  getTotalAmount,
  removeCartItem,
  type CartItemType,
} from "../../redux/slice/useCartSlice";
import { Color } from "../../utils/theme";

import Empty from "../../assets/images/appImages/category.png";

/* ── Types ── */
export interface ProductCardProps {
  item: any;
  devise?: string;
  /** Si fourni, un clic sur la carte navigue vers ce chemin (remplace le comportement par défaut) */
  onCardClick?: (item: any) => void;
}

const getCartVariations = (item: any): any[] => {
  if (!item?.variations || item.variations.length === 0) return [];
  const first =
    item.variations.find((v: any) => isVariationInStock(v)) ||
    item.variations[0];
  return first ? [first] : [];
};

/* ── Composant ── */
const ProductCard = ({ item, devise, onCardClick }: ProductCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { cartItems } = useAppSelector((s) => s?.cart);

  const mustChooseVariation = needsVariationSelection(item);
  const selectionLabel = getCatalogSelectionLabel(item);
  const tvaSettings = useShopTvaSettings();
  const priceInfo = getCatalogPriceInfo(item, tvaSettings);
  const { hasPromo, promoTaux } = priceInfo;

  /* --- Image --- */
  const imgProd =
    item?.type === "taille_unique"
      ? item?.images?.[0]?.image
      : item?.variations?.[0]?.images?.[0]?.image ?? null;

  const cartVariations = getCartVariations(item);

  /* --- Stock --- */
  const outOfStock = !hasProductStock(item);

  /* --- Panier --- */
  const isInCart = (): boolean =>
    cartItems?.some(
      (cartItem: CartItemType) =>
        cartItem?.produit?.id == item?.id &&
        (cartItem?.variations?.[0]?.id ?? null) ==
          (cartVariations?.[0]?.id ?? null)
    ) ?? false;

  const getCartItem = (): CartItemType | undefined =>
    cartItems?.find(
      (cartItem: CartItemType) =>
        cartItem?.produit?.id == item?.id &&
        (cartItem?.variations?.[0]?.id ?? null) ==
          (cartVariations?.[0]?.id ?? null)
    );

  const handleToggleCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (outOfStock) return;

    if (isInCart()) {
      const cartItem = getCartItem();
      if (cartItem) {
        dispatch(removeCartItem(cartItem));
        dispatch(getCartCount());
        dispatch(getTotalAmount());
        Swal.fire({
          icon: "info",
          iconColor: Color.success,
          confirmButtonColor: Color.success,
          title: "Produit retiré du panier",
          showConfirmButton: false,
          timer: 2000,
        });
      }
      return;
    }

    const variation = cartVariations[0];
    const itemStock =
      variation != null
        ? parseLocaleNumber(variation.quantite)
        : typeof item?.stock === "number"
          ? item.stock
          : Number(item?.stock) || 0;

    const produit: CartItemType = {
      produit: item,
      prix: priceInfo.displayPrice,
      quantite: 1,
      stock: itemStock,
      variations: cartVariations,
      vendeur: item?.vendeur?.id,
    };

    dispatch(addCartProduct(produit));
    dispatch(getCartCount());
    dispatch(getTotalAmount());
    Swal.fire({
      icon: "success",
      iconColor: Color.success,
      confirmButtonColor: Color.success,
      title: "Produit ajouté au panier avec succès !",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  /* --- Navigation --- */
  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(item);
    } else {
      navigate(`/produit/${item?.slug}`);
    }
  };

  const handleChooseVariation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!outOfStock) {
      handleCardClick();
    }
  };

  const inCart = !mustChooseVariation && isInCart();
  const buyLabel = selectionLabel ?? "Ajouter au panier";

  const renderPrice = () => {
    if (priceInfo.isFromPrice) {
      if (hasPromo) {
        return (
          <>
            <span className="product-card__price product-card__price--current">
              À partir de {formatCurrency(priceInfo.displayPrice, devise)}
            </span>
            <span className="product-card__price product-card__price--old">
              À partir de {formatCurrency(priceInfo.originalPrice, devise)}
            </span>
          </>
        );
      }
      return (
        <span className="product-card__price product-card__price--current">
          À partir de {formatCurrency(priceInfo.displayPrice, devise)}
        </span>
      );
    }

    if (hasPromo) {
      return (
        <>
          <span className="product-card__price product-card__price--current">
            {formatCurrency(priceInfo.displayPrice, devise)}
          </span>
          <span className="product-card__price product-card__price--old">
            {formatCurrency(priceInfo.originalPrice, devise)}
          </span>
        </>
      );
    }

    return (
      <span className="product-card__price product-card__price--current">
        {formatCurrency(priceInfo.displayPrice, devise)}
      </span>
    );
  };

  /* --- Render --- */
  return (
    <div
      className={[
        "product-card",
        hasPromo && !outOfStock ? "product-card--has-promo" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleCardClick}
      style={{ cursor: "pointer" }}
    >
      {/* Bouton rapide panier */}
      <button
        onClick={mustChooseVariation ? handleChooseVariation : handleToggleCart}
        className={`product-card__quick-add${inCart ? " product-card__quick-add--added" : ""}`}
        title={
          mustChooseVariation
            ? selectionLabel ?? "Choisir une variation"
            : inCart
              ? "Retirer du panier"
              : "Ajouter au panier"
        }
        disabled={outOfStock}
      >
        {inCart ? (
          <BsCartCheck size={16} />
        ) : (
          <FiShoppingCart size={16} color="#0B3D68" />
        )}
      </button>

      {/* Image + badges */}
      <div className="product-card__image-wrapper">
        {(outOfStock || hasPromo || item?.new === true) && (
          <div className="product-card__badges" aria-hidden>
            {outOfStock && (
              <span className="product-card__badge product-card__badge--rupture">
                Rupture
              </span>
            )}
            {hasPromo && !outOfStock && (
              <span className="product-card__badge product-card__badge--promo">
                −{Math.round(promoTaux)}%
              </span>
            )}
            {item?.new === true && !outOfStock && !hasPromo && (
              <span className="product-card__badge product-card__badge--new">
                Nouveau
              </span>
            )}
          </div>
        )}
        <img
          src={imgProd ? getImage(imgProd) : Empty}
          alt={item?.nom ?? ""}
          className="product-card__image"
          loading="lazy"
        />
      </div>

      {/* Corps */}
      <div className="product-card__body">
        <p className="product-card__name">{truncateCaractere(item?.nom, 39)}</p>

        <div
          className={[
            "product-card__price-wrapper",
            hasPromo ? "product-card__price-wrapper--promo" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {renderPrice()}
        </div>

        <button
          onClick={mustChooseVariation ? handleChooseVariation : handleToggleCart}
          className={[
            "product-card__buy-btn",
            inCart ? "product-card__buy-btn--added" : "",
            outOfStock ? "product-card__buy-btn--disabled" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          disabled={outOfStock}
        >
          {buyLabel}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
